import React, { Fragment, useState, useMemo } from 'react'
import {
  BlockStack,
  Box,
  Button,
  Card,
  Collapsible,
  Divider,
  InlineGrid,
  Text,
  Tooltip,
} from '@shopify/polaris'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  LockIcon,
  SettingsIcon,
} from '@shopify/polaris-icons'
import { useTranslation } from 'react-i18next'
import { useGlobalRecommendations } from '../../../../hooks/useGlobalRecommendations'

import * as WIDGETS from '../../../../constants/widgets'
import { FEATURES } from '../../../../constants/features'
import { areFeaturesEnabled } from '../../../../utils/features'

function Configure({ pricing, backActionUrl }) {
  const { t } = useTranslation()
  return (
    <BlockStack gap={'200'}>
      {WIDGET_TYPES.map((widget) => (
        <RecommendationCard
          backActionUrl={backActionUrl}
          key={widget.value}
          title={t(`Widgets.${widget.value}.label`)}
          description={t(`Widgets.${widget.value}.description`)}
          icon={widget.icon}
          widgetType={widget.value}
          pricing={pricing}
        />
      ))}
    </BlockStack>
  )
}

export default Configure

function RecommendationCard({
  title,
  description,
  icon,
  widgetType,
  pricing,
  backActionUrl,
}) {
  const [collapsed, setCollapsed] = useState(true)
  const { isLoading, count } = useGlobalRecommendations(widgetType)
  const filteredSettings = useMemo(() => {
    return ALL_SETTINGS.filter((setting) => {
      if (setting === 'globalRecommendation') {
        return !isLoading && count > 0
      }
      return true
    })
  }, [isLoading, count])

  return (
    <Card padding={'0'}>
      <BlockStack>
        <div
          style={{
            padding: 'var(--p-space-400)',
            cursor: 'pointer',
          }}
          onClick={() => setCollapsed(!collapsed)}
        >
          <InlineGrid columns={'1fr auto'} gap={'400'}>
            <InlineGrid columns={'auto 1fr'} gap={'400'}>
              <img src={icon} width={32} height={32} alt="icon" />
              <BlockStack gap={'100'}>
                <Text as="h6" fontWeight="semibold">
                  {title}
                </Text>
                <Text as="p">{description}</Text>
              </BlockStack>
            </InlineGrid>
            <Button
              variant="plain"
              icon={collapsed ? ChevronDownIcon : ChevronUpIcon}
            />
          </InlineGrid>
        </div>
        <Collapsible open={!collapsed}>
          <Divider />
          <div
            style={{
              width: '100%',
              padding: 'var(--p-space-400)',
            }}
          >
            <InlineGrid gap={'400'} columns={1}>
              {filteredSettings
                .sort((a, b) => {
                  const isFirstEnabled =
                    WidgetToSettingMap[widgetType].indexOf(a) !== -1
                  const isSecondEnabled =
                    WidgetToSettingMap[widgetType].indexOf(b) !== -1
                  if (isFirstEnabled && !isSecondEnabled) {
                    return -1
                  }
                  if (!isFirstEnabled && isSecondEnabled) {
                    return 1
                  }
                  return 0
                })
                .map((setting) => (
                  <RecommendationSettingCard
                    key={setting}
                    disabled={
                      WidgetToSettingMap[widgetType].indexOf(setting) === -1
                    }
                    backActionUrl={backActionUrl}
                    type={setting}
                    widgetType={widgetType}
                    pricing={pricing}
                  />
                ))}
            </InlineGrid>
          </div>
        </Collapsible>
      </BlockStack>
    </Card>
  )
}

function RecommendationSettingCard({
  disabled,
  type,
  widgetType,
  pricing,
  backActionUrl,
}) {
  const { t } = useTranslation()
  const { isLoading, count } = useGlobalRecommendations(widgetType)
  if (type === 'globalRecommendation' && !isLoading && count <= 0) {
    return null
  }
  const featureEnabled =
    type !== 'ruleBasedRecommendation' ||
    areFeaturesEnabled([FEATURES.RECOMMENDATION_RULES], window.shopify.data)
  const Wrapper = disabled || !featureEnabled ? Tooltip : Fragment

  const title = t(`Recommendation.${type}.title`)
  const description = t(`Recommendation.${type}.description`)
  const url = `/settings/recommendation/${widgetType}/${SettingToUrlSlug[type]}`
  const btnText = t(`Recommendation.btnText`)

  return (
    <div
      style={{
        padding: 'var(--p-space-400)',
        borderRadius: 'var(--p-border-radius-400)',
        border: 'var(--p-border-width-025) solid var(--p-color-border)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 'var(--p-space-200)',
          flex: 1,
        }}
      >
        <Text as="strong">{title}</Text>
        <Text>{description}</Text>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Wrapper
          {...(disabled || !featureEnabled
            ? {
                content:
                  (disabled && t('DefaultText.settingNotAvailable')) ||
                  (!featureEnabled &&
                    t('Features.notAvailableInPlanText', {
                      feature: t('Features.labels.recommednation_rules'),
                      cta: '',
                    })),
              }
            : {})}
        >
          <Button
            loading={pricing.isLoading && !featureEnabled}
            url={
              featureEnabled &&
              `${url}${backActionUrl ? `?back=${backActionUrl}` : ''}`
            }
            disabled={disabled}
            icon={featureEnabled ? SettingsIcon : LockIcon}
            onClick={() => {
              if (featureEnabled) {
                return
              }
              pricing.open({
                features: [FEATURES.RECOMMENDATION_RULES],
              })
            }}
          >
            {btnText}
          </Button>
        </Wrapper>
      </div>
    </div>
  )
}

const WIDGET_TYPES = [
  WIDGETS.SIMILAR_PRODUCTS,
  WIDGETS.BOUGHT_TOGETHER,
  WIDGETS.CROSS_SELL,
  WIDGETS.TRENDING,
  WIDGETS.BESTSELLERS,
]

const WidgetToSettingMap = {
  [WIDGETS.SIMILAR_PRODUCTS.value]: [
    'manualRecommendation',
    'globalRecommendation',
  ],
  [WIDGETS.BOUGHT_TOGETHER.value]: [
    'manualRecommendation',
    'ruleBasedRecommendation',
    'globalRecommendation',
  ],
  [WIDGETS.CROSS_SELL.value]: [
    'manualRecommendation',
    'ruleBasedRecommendation',
    'globalRecommendation',
  ],
  [WIDGETS.TRENDING.value]: ['globalRecommendation'],
  [WIDGETS.BESTSELLERS.value]: ['globalRecommendation'],
}

const SettingToUrlSlug = {
  manualRecommendation: 'product',
  ruleBasedRecommendation: 'rule',
  globalRecommendation: 'global',
}

const ALL_SETTINGS = [
  'manualRecommendation',
  'ruleBasedRecommendation',
  'globalRecommendation',
]
